import AddIcon from "@mui/icons-material/Add";
import PublishIcon from "@mui/icons-material/Publish";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Grid from "@mui/joy/Grid";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import React from "react";
import { DocumentWithDetails } from "../../../models/document";
import { Person } from "../../../models/person";
import { TaskId } from "../../../models/task";
import TableSkeleton from "../../../shared/components/feedback/skeletons/TableSkeleton";
import { useSnackbar } from "../../../shared/components/layout/SnackbarContext";
import TitleStylizedUnderline from "../../../shared/components/ui/TitleStylizedUnderline";
import {
  DocumentQuartzClientContext,
  TaskQuartzClientContext,
} from "../../../shared/services/api/contexts";
import DocumentFilters, { ReviewStatus } from "../components/DocumentFilters";
import { DocumentUploadArea } from "../components/DocumentUploadArea";
import { DocumentUploadStatus, UploadTask } from "../components/DocumentUploadStatus";
import { DocumentsList } from "../components/DocumentsList";

interface DocumentsProps {
  documentList?: DocumentWithDetails[];
  personList?: Person[];
  loading?: boolean;
}

export const Documents = ({ documentList, personList, loading = true }: DocumentsProps) => {
  // Contexts
  const { showSnackbar } = useSnackbar();
  const documentQuartzClient = React.useContext(DocumentQuartzClientContext);
  const taskQuartzClient = React.useContext(TaskQuartzClientContext);

  // States
  const [persons, setPersons] = React.useState<Person[] | undefined>(personList);
  const [documents, setDocuments] = React.useState<DocumentWithDetails[] | undefined>(documentList);
  const [isLoading, setIsLoading] = React.useState(loading);
  const [refreshTime, setRefreshTime] = React.useState(new Date().getTime());
  const [uploads, setUploads] = React.useState<UploadTask[]>([]);
  const [reviewFilter, setReviewFilter] = React.useState<ReviewStatus>("needs-review");
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const refresh = () => {
    setRefreshTime(new Date().getTime());
  };

  const handleReviewFilterChange = (status: ReviewStatus) => {
    setReviewFilter(status);
    setRefreshTime(new Date().getTime()); // Trigger refresh with new filter
  };

  const handleDocumentUpload = async (files: File[]) => {
    const newUploads: UploadTask[] = [];

    for (const file of files) {
      try {
        // Convert file to base64
        const base64Content = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
            const base64 = result.split(",")[1];
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        // Create upload request
        const uploadRequest = {
          content_type: file.type,
          filename: file.name,
          content: base64Content,
          reviewed: false, // New uploads are not reviewed by default
        };

        // Start upload
        const task = await documentQuartzClient.postDocument(uploadRequest);

        newUploads.push({
          ...task,
          file,
        });

        showSnackbar({
          text: `Started uploading ${file.name}`,
          color: "primary",
        });
      } catch (error: any) {
        showSnackbar({
          text: `Failed to upload ${file.name}: ${error.message}`,
          color: "danger",
        });
      }
    }

    // Add new uploads to state
    setUploads((prev) => [...prev, ...newUploads]);

    // Start polling for upload status
    newUploads.forEach((upload) => {
      pollTaskStatus(upload.task_id);
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      handleDocumentUpload(files);
    }
    // Reset the input value so the same file can be selected again
    e.target.value = "";
  };

  const handleUploadButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const pollTaskStatus = async (taskId: TaskId) => {
    try {
      const task = await taskQuartzClient.getTaskById(taskId);

      setUploads((prev) =>
        prev.map((upload) => (upload.task_id === taskId ? { ...upload, ...task } : upload))
      );

      // If task is still running, poll again
      if (task.status === "requested" || task.status === "in-progress") {
        setTimeout(() => pollTaskStatus(taskId), 2000);
      } else if (task.status === "completed") {
        // Refresh documents list when upload completes
        refresh();
        showSnackbar({
          text: "Document uploaded successfully",
          color: "success",
        });
      } else if (task.status === "failed") {
        showSnackbar({
          text: `Upload failed: ${task.status_reason}`,
          color: "danger",
        });
      }
    } catch (error: any) {
      console.error("Failed to poll task status:", error);
    }
  };

  const handleRemoveCompleted = (taskId: TaskId) => {
    setUploads((prev) => prev.filter((upload) => upload.task_id !== taskId));
  };

  const handleCloseUploadStatus = () => {
    // Only remove completed and failed uploads
    setUploads((prev) =>
      prev.filter((upload) => upload.status === "requested" || upload.status === "in-progress")
    );
  };

  React.useEffect(() => {
    const getDocuments = async () => {
      setIsLoading(true);
      try {
        const filterValue = reviewFilter === "all" ? undefined : reviewFilter;
        const documentsWithDetails = await documentQuartzClient.listRecentDocumentsWithDetails(
          25,
          filterValue
        );
        setDocuments(documentsWithDetails);
        // TODO: Load persons if needed (though now we get patient names directly)
        setPersons([]);
      } catch (e: any) {
        showSnackbar({ text: e.message, color: "danger" });
      } finally {
        setIsLoading(false);
      }
    };

    getDocuments();
  }, [showSnackbar, refreshTime, documentQuartzClient, reviewFilter]);

  // Global drag and drop handlers for when documents exist
  const [isDragOver, setIsDragOver] = React.useState(false);

  const handleGlobalDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleGlobalDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the main container
    if (e.currentTarget === e.target) {
      setIsDragOver(false);
    }
  };

  const handleGlobalDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleDocumentUpload(files);
    }
  };

  const hasDocuments = documents && documents.length > 0;

  return (
    <Box
      sx={{ p: 4 }}
      onDragOver={hasDocuments ? handleGlobalDragOver : undefined}
      onDragLeave={hasDocuments ? handleGlobalDragLeave : undefined}
      onDrop={hasDocuments ? handleGlobalDrop : undefined}
    >
      {/* Drag overlay for when documents exist */}
      {hasDocuments && isDragOver && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 123, 255, 0.1)",
            border: "3px dashed rgba(0, 123, 255, 0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <Typography level="h2" color="primary">
            Drop files to upload
          </Typography>
        </Box>
      )}

      <Stack
        sx={{ width: "100%" }}
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Stack direction="column" spacing={1}>
          <Typography level="h2" sx={{ display: "inline-block" }}>
            <TitleStylizedUnderline color="rgb(234, 154, 62)">Documents</TitleStylizedUnderline>
          </Typography>
          {hasDocuments && (
            <Typography level="body-sm" color="neutral">
              <PublishIcon sx={{ fontSize: "1rem", mr: 0.5, verticalAlign: "middle" }} />
              Drop files anywhere or use the upload button
            </Typography>
          )}
        </Stack>
        {hasDocuments && (
          <Stack direction="row" spacing={1}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.txt"
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
            <Button startDecorator={<AddIcon />} onClick={handleUploadButtonClick}>
              Upload Documents
            </Button>
          </Stack>
        )}
      </Stack>

      <Grid xs={12} sx={{ mt: 4 }}>
        {isLoading ? (
          <Box sx={{ height: "70vh", width: "100%", overflow: "hidden", position: "relative" }}>
            <TableSkeleton />
          </Box>
        ) : (
          <Stack direction="column" spacing={3}>
            {hasDocuments ? (
              // When documents exist, show filters and documents list
              <>
                <DocumentFilters
                  defaultReviewStatus={reviewFilter}
                  onReviewStatusChange={handleReviewFilterChange}
                />
                <DocumentsList documents={documents} persons={persons} />
              </>
            ) : (
              // When no documents, show upload area instead of the illustration
              <DocumentUploadArea onUpload={handleDocumentUpload} />
            )}
          </Stack>
        )}
      </Grid>

      {/* Upload status overlay */}
      <DocumentUploadStatus
        uploads={uploads}
        onRemoveCompleted={handleRemoveCompleted}
        onClose={handleCloseUploadStatus}
      />
    </Box>
  );
};
